"use client";

/**
 * useAssessment — drives starting a new assessment run.
 *
 * Flow (mirrors React DiagnosticsPage.handleStart):
 *   1. Load questions (static JSON or dynamic API with localStorage cache)
 *   2. Create a DiagnosticsSession in Firestore
 *   3. Navigate to /diagnostics/saved/${sessionId}/questions
 *
 * No backend "start" endpoint is called.
 * Questions loading uses the same logic as the React version.
 */
import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/platform/auth/useAuth";
import { loadDiagnosticQuestions } from "@/modes/diagnostics/api";
import { createSession } from "@/platform/firebase/firestore/diagnostics";
import { DIAGNOSTICS } from "@/modes/diagnostics/data";
import type { DiagnosticTemplate } from "@/modes/diagnostics/data";

export type StartStatus = "idle" | "loading" | "error";

interface UseAssessmentResult {
  status:  StartStatus;
  error:   string | null;
  start:   (diagnosticId: string) => Promise<void>;
  /** For custom diagnostics injected at runtime */
  startCustom: (diagnostic: DiagnosticTemplate) => Promise<void>;
}

export function useAssessment(): UseAssessmentResult {
  const { user } = useAuth();
  const router   = useRouter();

  const [status, setStatus] = useState<StartStatus>("idle");
  const [error,  setError]  = useState<string | null>(null);

  const startWithTemplate = useCallback(async (diag: DiagnosticTemplate) => {
    setStatus("loading");
    setError(null);

    try {
      // 1. Load questions (static or dynamic, with cache)
      const questions = await loadDiagnosticQuestions(diag);

      // 2. Build a session title
      const now   = new Date();
      const title = `${diag.title} - ${now.toLocaleDateString()}`;

      let sessionId: string;

      if (user) {
        // 3a. Authenticated: create Firestore session
        sessionId = await createSession(user.uid, {
          title,
          diagnosticId:    diag.id,
          diagnosticTitle: diag.title,
          questions,
        });
      } else {
        // 3b. Unauthenticated: persist to localStorage (React parity)
        sessionId = `saved-${Date.now()}`;
        const session = {
          id:               sessionId,
          title,
          diagnosticId:     diag.id,
          diagnosticTitle:  diag.title,
          questions,
          answers:          {},
          answer_authors:   {},
          analysis:         null,
          analysisMarkdown: "",
          score:            0,
          completedAt:      now.toISOString(),
          isStarred:        false,
          isArchived:       false,
          isCompleted:      false,
          currentQuestion:  0,
          createdAt:        now.toISOString(),
          created_by_uid:   "",
          shared_with:      [],
          invited_emails:   [],
          resolved_uids:    [],
        };
        const raw     = localStorage.getItem("savedDiagnostics");
        const existing = raw ? JSON.parse(raw) : [];
        localStorage.setItem("savedDiagnostics", JSON.stringify([session, ...existing]));
      }

      // 4. Navigate to the questions page
      router.push(`/diagnostics/saved/${sessionId}/questions`);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Failed to start assessment");
    }
  }, [user, router]);

  const start = useCallback(async (diagnosticId: string) => {
    const diag = DIAGNOSTICS.find((d) => d.id === diagnosticId);
    if (!diag) {
      setError(`Unknown diagnostic: ${diagnosticId}`);
      return;
    }
    await startWithTemplate(diag);
  }, [startWithTemplate]);

  const startCustom = useCallback(async (diagnostic: DiagnosticTemplate) => {
    await startWithTemplate(diagnostic);
  }, [startWithTemplate]);

  return { status, error, start, startCustom };
}
